<script setup lang="ts">
/**
 * App-Rahmen: schwarze Kopfleiste mit Logo, Wortmarke und Navigation.
 *
 * Die Shell verschwindet vollständig, wenn
 *  - die Route `meta.chrome === false` setzt (Anmeldung, Druckansichten) oder
 *  - niemand angemeldet ist.
 *
 * Unter 900px ersetzt ein Drawer die Navigationsleiste (mit Scroll-Lock,
 * schließt bei jeder Navigation).
 */
import Drawer from 'primevue/drawer'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'

import { useLogoutMutation } from '@/api/queries'
import FlashToaster from '@/components/shell/FlashToaster.vue'
import { toast } from '@/composables/toast'
import { de } from '@/strings/de'
import { useAuthStore } from '@/stores/auth'

const MOBILE_QUERY = '(max-width: 899px)'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const logoutMutation = useLogoutMutation()

const drawerOpen = ref(false)
const isMobile = ref(false)

let mediaQuery: MediaQueryList | null = null

function applyMatch(event: MediaQueryList | MediaQueryListEvent): void {
  isMobile.value = event.matches
  if (!event.matches) drawerOpen.value = false
}

onMounted(() => {
  if (typeof window === 'undefined' || !window.matchMedia) return
  mediaQuery = window.matchMedia(MOBILE_QUERY)
  applyMatch(mediaQuery)
  mediaQuery.addEventListener('change', applyMatch)
})

onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', applyMatch)
  document.body.classList.remove('selwa-scroll-locked')
})

// Scroll-Lock, solange der Drawer offen ist.
watch(drawerOpen, (open) => {
  document.body.classList.toggle('selwa-scroll-locked', open)
})

// Jede Navigation schließt den Drawer – Inhalte bleiben so immer erreichbar.
watch(
  () => route.fullPath,
  () => {
    drawerOpen.value = false
  },
)

interface NavItem {
  key: string
  label: string
  to: RouteLocationRaw
  /** Aktive Route(n) für die Hervorhebung. */
  match: string[]
  adminOnly?: boolean
}

const allNavItems: NavItem[] = [
  {
    key: 'patients',
    label: de.nav.patients,
    to: { name: 'patients' },
    match: ['patients', 'patient-detail'],
  },
  { key: 'patient-new', label: de.nav.newPatient, to: { name: 'patient-new' }, match: ['patient-new'] },
  { key: 'license', label: de.nav.license, to: { name: 'license' }, match: ['license'] },
  {
    key: 'admin',
    label: de.nav.administration,
    to: { name: 'admin-therapists' },
    match: ['admin-therapists', 'admin-licenses'],
    adminOnly: true,
  },
  { key: 'imprint', label: de.nav.imprint, to: { name: 'imprint' }, match: ['imprint'] },
]

const navItems = computed(() => allNavItems.filter((item) => !item.adminOnly || auth.isAdmin))

const showChrome = computed(() => route.meta.chrome !== false && auth.isAuthenticated)

function isActive(item: NavItem): boolean {
  return item.match.includes(String(route.name ?? ''))
}

async function onSignOut(): Promise<void> {
  drawerOpen.value = false
  try {
    await logoutMutation.mutateAsync()
  } catch {
    // Abmelden ist auch bei Serverfehlern lokal wirksam.
  }
  auth.clear()
  toast.info(de.auth.signedOut)
  await router.push({ name: 'signin' })
}
</script>

<template>
  <div class="app-shell" :class="{ 'app-shell--bare': !showChrome }">
    <header v-if="showChrome" class="app-shell__topbar">
      <div class="app-shell__bar selwa-container selwa-container--wide">
        <RouterLink class="app-shell__brand" :to="{ name: 'patients' }">
          <img class="app-shell__logo" src="/img/logo.jpg" :alt="de.app.logoAlt" />
          <span class="app-shell__wordmark">{{ de.app.name }}</span>
        </RouterLink>

        <nav v-if="!isMobile" class="app-shell__nav" :aria-label="de.nav.menuTitle">
          <RouterLink
            v-for="item in navItems"
            :key="item.key"
            class="app-shell__nav-item"
            :class="{ 'app-shell__nav-item--active': isActive(item) }"
            :aria-current="isActive(item) ? 'page' : undefined"
            :to="item.to"
          >
            {{ item.label }}
          </RouterLink>
        </nav>

        <div class="app-shell__spacer" />

        <button
          v-if="!isMobile"
          type="button"
          class="app-shell__signout"
          @click="onSignOut"
        >
          <i class="pi pi-sign-out" aria-hidden="true" />
          <span>{{ de.nav.signOut }}</span>
        </button>

        <button
          v-else
          type="button"
          class="app-shell__hamburger"
          :aria-label="de.nav.openMenu"
          :aria-expanded="drawerOpen"
          @click="drawerOpen = true"
        >
          <i class="pi pi-bars" aria-hidden="true" />
        </button>
      </div>
    </header>

    <Drawer
      v-if="showChrome"
      v-model:visible="drawerOpen"
      position="right"
      class="app-shell__drawer"
      :header="de.nav.menuTitle"
      :block-scroll="true"
    >
      <nav class="app-shell__drawer-nav" :aria-label="de.nav.menuTitle">
        <RouterLink
          v-for="item in navItems"
          :key="item.key"
          class="app-shell__drawer-item"
          :class="{ 'app-shell__drawer-item--active': isActive(item) }"
          :aria-current="isActive(item) ? 'page' : undefined"
          :to="item.to"
          @click="drawerOpen = false"
        >
          {{ item.label }}
        </RouterLink>
        <button type="button" class="app-shell__drawer-item" @click="onSignOut">
          {{ de.nav.signOut }}
        </button>
      </nav>
    </Drawer>

    <main class="app-shell__main" :class="{ 'app-shell__main--framed': showChrome }">
      <slot />
    </main>

    <FlashToaster />
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-shell__topbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--selwa-ink);
  color: #fff;
  box-shadow: var(--selwa-shadow-2);
}

.app-shell__bar {
  display: flex;
  align-items: center;
  gap: var(--selwa-space-5);
  min-height: var(--selwa-topbar-height);
}

.app-shell__spacer {
  flex: 1 1 auto;
}

.app-shell__brand {
  display: inline-flex;
  align-items: center;
  gap: var(--selwa-space-2);
  text-decoration: none;
  color: inherit;
  flex: 0 0 auto;
}

.app-shell__logo {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  object-fit: cover;
  background: #fff;
}

.app-shell__wordmark {
  font-family: var(--selwa-font-display);
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: 0.02em;
}

.app-shell__nav {
  display: flex;
  align-items: center;
  gap: var(--selwa-space-1);
  min-width: 0;
  overflow-x: auto;
}

.app-shell__nav-item {
  position: relative;
  padding: 0.4rem 0.75rem;
  border-radius: var(--selwa-radius-sm);
  color: rgb(255 255 255 / 82%);
  text-decoration: none;
  white-space: nowrap;
  font-weight: 500;
}

.app-shell__nav-item:hover {
  color: #fff;
  background: rgb(255 255 255 / 8%);
}

.app-shell__nav-item--active {
  color: #fff;
}

.app-shell__nav-item--active::after {
  content: '';
  position: absolute;
  left: 0.75rem;
  right: 0.75rem;
  bottom: -0.15rem;
  height: 3px;
  border-radius: 2px;
  background: var(--selwa-amber);
}

.app-shell__signout,
.app-shell__hamburger {
  display: inline-flex;
  align-items: center;
  gap: var(--selwa-space-2);
  background: transparent;
  border: 1px solid rgb(255 255 255 / 22%);
  color: #fff;
  font: inherit;
  padding: 0.35rem 0.75rem;
  border-radius: var(--selwa-radius-sm);
  cursor: pointer;
}

.app-shell__signout:hover,
.app-shell__hamburger:hover {
  background: rgb(255 255 255 / 10%);
}

.app-shell__hamburger {
  font-size: 1.15rem;
  padding: 0.35rem 0.6rem;
}

.app-shell__drawer-nav {
  display: flex;
  flex-direction: column;
  gap: var(--selwa-space-1);
}

.app-shell__drawer-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: var(--selwa-space-3);
  border: 0;
  border-radius: var(--selwa-radius-sm);
  background: transparent;
  color: var(--selwa-text);
  font: inherit;
  text-decoration: none;
  cursor: pointer;
}

.app-shell__drawer-item:hover {
  background: var(--selwa-paper);
}

.app-shell__drawer-item--active {
  background: var(--selwa-amber-soft);
  font-weight: 600;
}

.app-shell__main {
  flex: 1 1 auto;
}

.app-shell__main--framed {
  padding-block: var(--selwa-space-6);
}
</style>
